/**
 * 草图曲线求交的纯数学工具函数集。
 * 职责：曲线求交过程中与草图实体对象无关的纯数值计算——
 * 角度区间判定（isAngleInArcRange）与一元二/三/四次方程求根
 * （solveQuadratic/solveCubic/solveQuartic）。
 * 说明：本文件只含纯函数、不依赖任何草图实体类，供 SketchEntityIntersect
 * （①求解层）等求交模块直接调用。
 */

/**
 * 判断给定角度是否落在圆弧/椭圆弧扫掠范围内。
 * 说明：将所有角度规范化到 [0, 2π)，再按旋转方向判断（与 SketchArcObject.isAngleInArcRange 语义一致）。
 * @param angle 待判断角度（弧度）。
 * @param startAngle 弧起始角（弧度）。
 * @param endAngle 弧终止角（弧度）。
 * @param clockwise 是否为顺时针方向。
 * @returns 角度在弧范围内返回 true。
 */
export function isAngleInArcRange(
    angle: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean,
): boolean {
    const TWO_PI = Math.PI * 2;

    // 整周弧检测：起止角跨度 ≥ 2π（含容差）时为满圆/满椭圆，任意角度都在弧上。
    // 若不检测，normalize(2π) = 0 会使满圆区间坍缩为 [0, 0]"零度弧"，非 0° 交点全被剔除。
    if (Math.abs(endAngle - startAngle) >= TWO_PI - 1e-9) {
        return true;
    }

    // 将角度规范化到 [0, 2π)。
    const normalize = (a: number): number => ((a % TWO_PI) + TWO_PI) % TWO_PI;
    const a = normalize(angle);
    const s = normalize(startAngle);
    const e = normalize(endAngle);

    if (clockwise) {
        // 顺时针：从 s 递减到 e。
        if (s >= e) {
            return a <= s && a >= e;
        }
        // 跨越 0 点。
        return a <= s || a >= e;
    }
    // 逆时针：从 s 递增到 e。
    if (s <= e) {
        return a >= s && a <= e;
    }
    // 跨越 0 点。
    return a >= s || a <= e;
}

/**
 * 求解一元二次方程 a·x² + b·x + c = 0 的全部实数根。
 *
 * 实现说明：
 * 1. 最高次系数退化时降为一次方程；
 * 2. 判别式 < -1e-10 视为无实根；重根（判别式 ≈ 0）仅返回一个根。
 *
 * @param a 二次项系数。
 * @param b 一次项系数。
 * @param c 常数项。
 * @returns 实数根列表（可能为空）。
 */
export function solveQuadratic(a: number, b: number, c: number): number[] {
    // 二次项系数退化：降为一次方程
    if (Math.abs(a) < 1e-12) {
        if (Math.abs(b) < 1e-12) {
            return []; // 退化：无解或无穷解
        }
        return [-c / b];
    }

    const discriminant = b * b - 4 * a * c;
    // 判别式量级参考（两项绝对值之和）：小系数几何（坐标 0.01~0.1 量级）下判别式
    // 绝对值可能远小于固定容差但符号明确，绝对容差会把两个不同实根误合并为重根，
    // 导致求交漏根，故改为相对容差判定
    const discriminantScale = b * b + Math.abs(4 * a * c);
    const discriminantTolerance = discriminantScale * 1e-12;
    if (discriminant < -discriminantTolerance) {
        return []; // 无实根
    }
    if (discriminant <= discriminantTolerance) {
        return [-b / (2 * a)]; // 重根（相对意义上）
    }

    const sqrtDisc = Math.sqrt(discriminant);
    return [(-b - sqrtDisc) / (2 * a), (-b + sqrtDisc) / (2 * a)];
}

/**
 * 求解一元三次方程 a·x³ + b·x² + c·x + d = 0 的全部实数根（Cardano 法）。
 *
 * 实现说明：
 * 1. 最高次系数退化时降为二次方程；
 * 2. Δ > 0：一个实根（Cardano 公式）；
 * 3. Δ ≈ 0：三个实根（含二重根）；
 * 4. Δ < 0：三个不同实根（三角法，保证数值稳定）。
 *
 * @param a 三次项系数。
 * @param b 二次项系数。
 * @param c 一次项系数。
 * @param d 常数项。
 * @returns 实数根列表（可能为空，重根会重复返回）。
 */
export function solveCubic(a: number, b: number, c: number, d: number): number[] {
    // 三次项系数退化：降为二次方程
    if (Math.abs(a) < 1e-12) {
        return solveQuadratic(b, c, d);
    }

    // 归一化：x³ + A·x² + B·x + C = 0
    const A = b / a;
    const B = c / a;
    const C = d / a;

    // 平移消去二次项：x = y - A/3，得 y³ + p·y + q = 0
    const p = B - (A * A) / 3;
    const q = (2 * A * A * A) / 27 - (A * B) / 3 + C;

    // 判别式 Δ = (q/2)² + (p/3)³
    const delta = (q * q) / 4 + (p * p * p) / 27;
    // delta 量级参考（两项绝对值之和）：小系数几何（坐标 0.01~0.1 量级）下 delta
    // 绝对值可能远小于固定容差 1e-12 但符号明确，绝对容差会把"一个实根/三个实根"
    // 误判为"重根"产生假根，导致后续 Ferrari 分解失败、求交漏根，故改为相对容差判定
    const deltaScale = Math.abs((q * q) / 4) + Math.abs((p * p * p) / 27);
    const deltaTolerance = deltaScale * 1e-12;
    const roots: number[] = [];

    if (delta > deltaTolerance) {
        // 一个实根：Cardano 公式
        const sqrtDelta = Math.sqrt(delta);
        const u = Math.cbrt(-q / 2 + sqrtDelta);
        const v = Math.cbrt(-q / 2 - sqrtDelta);
        roots.push(u + v - A / 3);
    } else if (delta < -deltaTolerance) {
        // 三个不同实根：三角法（p 必为负）
        const sqrtMp3 = Math.sqrt(-p / 3);
        const phi = Math.acos(Math.max(-1, Math.min(1, (3 * q) / (2 * p) * Math.sqrt(-3 / p))));
        for (let k = 0; k < 3; k++) {
            roots.push(2 * sqrtMp3 * Math.cos(phi / 3 - (2 * Math.PI * k) / 3) - A / 3);
        }
    } else {
        // 相对意义上 delta ≈ 0：三重根或二重根
        const u = Math.cbrt(-q / 2);
        roots.push(2 * u - A / 3, -u - A / 3, -u - A / 3);
    }

    return roots;
}

/**
 * 求解一元四次方程 a4·x⁴ + a3·x³ + a2·x² + a1·x + a0 = 0 的全部实数根（Ferrari 法）。
 *
 * 实现说明：
 * 1. 最高次系数退化时降为三次方程；
 * 2. 平移消去三次项得 y⁴ + p·y² + q·y + r = 0；
 * 3. q ≈ 0 时退化为双二次方程直接求解；
 * 4. 一般情形：解 resolvent cubic 取满足 2m - p ≥ 0 的实根 m，
 *    将方程分解为两个二次方程求解，最后回代 x = y - a/4；
 * 5. 用于圆-椭圆、椭圆-椭圆等求交（t = tan(θ/2) 代换后的方程）。
 *
 * @param a4 四次项系数。
 * @param a3 三次项系数。
 * @param a2 二次项系数。
 * @param a1 一次项系数。
 * @param a0 常数项。
 * @returns 实数根列表（可能为空，重根会重复返回）。
 */
export function solveQuartic(a4: number, a3: number, a2: number, a1: number, a0: number): number[] {
    // 四次项系数退化：降为三次方程
    if (Math.abs(a4) < 1e-12) {
        return solveCubic(a3, a2, a1, a0);
    }

    // 归一化：x⁴ + a·x³ + b·x² + c·x + d = 0
    const a = a3 / a4;
    const b = a2 / a4;
    const c = a1 / a4;
    const d = a0 / a4;

    // 平移消去三次项：x = y - a/4，得 y⁴ + p·y² + q·y + r = 0
    const p = b - (3 * a * a) / 8;
    const q = c - (a * b) / 2 + (a * a * a) / 8;
    const r = d - (a * c) / 4 + (a * a * b) / 16 - (3 * a * a * a * a) / 256;

    // q ≈ 0：双二次方程 y⁴ + p·y² + r = 0（先解 z = y² 的二次方程）
    // q 的量级参考（|c| + |ab/2| + |a³/8|）：小系数几何下 q 绝对值可能远小于
    // 固定容差 1e-12 但并非可忽略，绝对容差会误走双二次近似导致漏根，
    // 故改为相对容差判定
    const qScale = Math.abs(c) + Math.abs((a * b) / 2) + Math.abs((a * a * a) / 8);
    if (Math.abs(q) <= qScale * 1e-12) {
        const ys: number[] = [];
        for (const z of solveQuadratic(1, p, r)) {
            if (z >= -1e-12) {
                const sqrtZ = Math.sqrt(Math.max(0, z));
                ys.push(sqrtZ, -sqrtZ);
            }
        }
        return ys.map((y) => y - a / 4);
    }

    // Ferrari 法：resolvent cubic —— m³ - (p/2)·m² - r·m + (pr/2 - q²/8) = 0
    // 取满足 2m - p ≥ 0 的实根 m，使 (y² + m)² 可分解为两个完全平方的差
    const mCandidates = solveCubic(
        1, -p / 2, -r, (p * r) / 2 - (q * q) / 8,
    );
    const m = mCandidates.find((candidate) => 2 * candidate - p >= -1e-9) ?? mCandidates[0] ?? 0;

    // 分解因子 s = √(2m - p)
    const s = Math.sqrt(Math.max(0, 2 * m - p));
    if (s < 1e-12) {
        return []; // 数值退化保护：无法完成分解（正常几何中不出现）
    }

    // 分解为两个二次方程：
    //   y² - s·y + (m + q/(2s)) = 0
    //   y² + s·y + (m - q/(2s)) = 0
    const roots: number[] = [];
    roots.push(...solveQuadratic(1, -s, m + q / (2 * s)));
    roots.push(...solveQuadratic(1, s, m - q / (2 * s)));

    // 回代 x = y - a/4
    return roots.map((y) => y - a / 4);
}
